import { RootState } from '../../store'
import { EventWithAdditionalInfo, EventFilters } from './types'
import { createSelector } from '@reduxjs/toolkit'

// Base selectors
export const selectTeamMemberEventData = (state: RootState) => state.teamMemberEvents.data
export const selectTeamMemberEventLoading = (state: RootState) => state.teamMemberEvents.loading
export const selectTeamMemberEventError = (state: RootState) => state.teamMemberEvents.error

// Specific data selectors
export const selectTeamMemberDetails = (state: RootState) =>
  state.teamMemberEvents.data?.teamMemberDetails
export const selectRegisteredEvents = (state: RootState) =>
  state.teamMemberEvents.data?.registeredEvents || []
export const selectUnregisteredEvents = (state: RootState) =>
  state.teamMemberEvents.data?.unregisteredEvents || []

// Computed selectors
export const selectTotalEvents = (state: RootState) => {
  const registered = state.teamMemberEvents.data?.registeredEvents?.length || 0
  const unregistered = state.teamMemberEvents.data?.unregisteredEvents?.length || 0
  return registered + unregistered
}

export const selectTotalPoints = (state: RootState) => {
  const registeredEvents = state.teamMemberEvents.data?.registeredEvents || []
  return registeredEvents
    .filter((event) => event.additionalInfo.isPointsAwarded)
    .reduce((total, event) => total + event.eventDetails.pointsNum, 0)
}

// Filter helpers
const filterByStatus = (events: EventWithAdditionalInfo[], status: string[]) =>
  status.length === 0 ? events : events.filter((event) => status.includes(event.eventDetails.status))

const filterByCategory = (events: EventWithAdditionalInfo[], category: string[]) =>
  category.length === 0
    ? events
    : events.filter((event) => category.includes(event.eventDetails.category))

const filterBySearch = (events: EventWithAdditionalInfo[], searchQuery: string) => {
  if (!searchQuery) return events

  const query = searchQuery.toLowerCase()
  return events.filter((event) => {
    const { eventDetails } = event
    return eventDetails.title.toLowerCase().includes(query)
  })
}

// Helper function to get date range based on days
const getDateRange = (days: number) => {
  const now = new Date()
  const start = new Date(now)
  start.setHours(0, 0, 0, 0)

  const end = new Date(now)
  end.setDate(now.getDate() + days)
  end.setHours(23, 59, 59, 999)

  return { start, end }
}

const filterByDateRange = (events: EventWithAdditionalInfo[], days: number) => {
  if (days === 0) return events // Return all events if no date filter

  const { start, end } = getDateRange(days)
  return events.filter((event) => {
    const eventStartDate = new Date(event.eventDetails.startDate)
    return eventStartDate >= start && eventStartDate <= end
  })
}

const filterByImportance = (events: EventWithAdditionalInfo[], importance: 'required' | 'optional') =>
  events.filter((event) => event.eventDetails.importance === importance)

const filterByArchived = (events: EventWithAdditionalInfo[], isArchived: boolean) =>
  events.filter((event) => event.eventDetails.isArchived === isArchived)

const filterByCompleted = (events: EventWithAdditionalInfo[], isCompleted: boolean) =>
  events.filter((event) => event.eventDetails.isCompleted === isCompleted)

// Sorting function
const sortByUpcomingStartDate = (events: EventWithAdditionalInfo[]) => {
  const now = new Date()
  return events.sort((a, b) => {
    const dateA = new Date(a.eventDetails.startDate)
    const dateB = new Date(b.eventDetails.startDate)
    const diffA = dateA.getTime() - now.getTime()
    const diffB = dateB.getTime() - now.getTime()

    // If both dates are in the future, sort normally
    if (diffA > 0 && diffB > 0) {
      return dateA.getTime() - dateB.getTime()
    }

    // If one date is in the future and the other is in the past, prioritize the future date
    if (diffA > 0) return -1
    if (diffB > 0) return 1

    // If both dates are in the past, sort in reverse order (most recent first)
    return dateB.getTime() - dateA.getTime()
  })
}

const applyFilters = (events: EventWithAdditionalInfo[], filters: EventFilters) => {
  let filteredEvents = events

  if (filters.status) {
    filteredEvents = filterByStatus(filteredEvents, filters.status)
  }

  if (filters.category) {
    filteredEvents = filterByCategory(filteredEvents, filters.category)
  }

  if (filters.searchQuery) {
    filteredEvents = filterBySearch(filteredEvents, filters.searchQuery)
  }

  if (filters.days !== undefined) {
    filteredEvents = filterByDateRange(filteredEvents, filters.days)
  }

  if (filters.importance) {
    filteredEvents = filterByImportance(filteredEvents, filters.importance)
  }

  if (filters.isArchived !== undefined) {
    filteredEvents = filterByArchived(filteredEvents, filters.isArchived)
  }

  if (filters.isCompleted !== undefined) {
    filteredEvents = filterByCompleted(filteredEvents, filters.isCompleted)
  }

  return sortByUpcomingStartDate(filteredEvents)
}

// Filtered selectors for registered events
export const selectFilteredRegisteredEvents = (state: RootState, filters: EventFilters) => {
  const registeredEvents = state.teamMemberEvents.data?.registeredEvents || []
  return applyFilters(registeredEvents, filters)
}

// Filtered selectors for unregistered events
export const selectFilteredUnregisteredEvents = (state: RootState, filters: EventFilters) => {
  const unregisteredEvents = state.teamMemberEvents.data?.unregisteredEvents || []
  return applyFilters(unregisteredEvents, filters)
}

// Filter selectors
export const selectRegisteredFilters = (state: RootState) => state.teamMemberEvents.filters.registered
export const selectUnregisteredFilters = (state: RootState) =>
  state.teamMemberEvents.filters.unregistered

// Filtered events using state filters
export const selectFilteredUnregisteredEventsNotArchivedAndNotCompletedAndActive = createSelector(
  [selectUnregisteredEvents, selectUnregisteredFilters],
  (unregisteredEvents, filters) => {
    const filteredEvents = applyFilters(unregisteredEvents, {
      status: ['active'],
      isArchived: false,
      isCompleted: false,
      category: filters.category || [],
      searchQuery: filters.searchQuery || '',
      days: filters.days || 0,
      ...filters,
    })

    return sortByUpcomingStartDate(filteredEvents)
  },
)

export const selectFilteredRegisteredEventsNotArchived = createSelector(
  [selectRegisteredEvents, selectRegisteredFilters],
  (registeredEvents, filters) => {
    const filteredEvents = applyFilters(registeredEvents, {
      isArchived: false,
      category: filters.category || [],
      searchQuery: filters.searchQuery || '',
      days: filters.days || 0,
      ...filters,
    })

    return sortByUpcomingStartDate(filteredEvents)
  },
)

// Convenience selectors for common date ranges
export const selectNextWeekEvents = createSelector([selectRegisteredEvents], (registeredEvents) =>
  filterByDateRange(registeredEvents, 7),
)

export const selectNextMonthEvents = createSelector([selectRegisteredEvents], (registeredEvents) =>
  filterByDateRange(registeredEvents, 30),
)

// Selector for all events sorted by upcoming start date
export const selectAllEventsSortedByUpcomingStartDate = createSelector(
  [selectRegisteredEvents, selectUnregisteredEvents],
  (registeredEvents, unregisteredEvents) => {
    const allEvents = [...registeredEvents, ...unregisteredEvents]
    return sortByUpcomingStartDate(allEvents)
  },
)

// Selector for all events sorted by upcoming start date and filtered by status
export const selectAllEventsSortedByUpcomingForEventSummary = createSelector(
  [selectAllEventsSortedByUpcomingStartDate],
  (allEvents) =>
    applyFilters(allEvents, {
      isArchived: false,
      isCompleted: false,
      status: ['active','inactive'],
    }),
)
